import { useEffect, useState, type ReactNode } from 'react';

/**
 * One attribute a chip can set. A field with a `panel` opens an inline drawer
 * under the chips (a date, a repeat, a note); a field with `onToggle` and no
 * panel is a flag the chip flips in place (needs-car, brings-child, all-day).
 */
export interface AttributeField {
  key: string;
  label: string;
  icon?: ReactNode;
  /** Shown in place of `label` once the field is set — a picked date, a name. */
  value?: ReactNode;
  /** Whether the field carries a value; a set chip wears the accent. */
  set: boolean;
  hidden?: boolean;
  panel?: ReactNode;
  onToggle?: () => void;
}

/**
 * A row of attribute chips over a single inline drawer — the quick way to set a
 * date, a repeat or a flag without a second trip into a sheet. Lifted out of the
 * todo quick-add so the agenda editor sets its secondary fields the same way
 * (shared through platform/, never a cross-module import — the independence
 * rule). One panel is open at a time; bumping `resetKey` closes it, so a form
 * can collapse the drawer when it submits.
 */
export function AttributeFields({
  fields,
  resetKey,
}: {
  fields: AttributeField[];
  resetKey?: unknown;
}) {
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => setOpen(null), [resetKey]);
  const shown = fields.filter((f) => !f.hidden);

  return (
    <div className="attr-fields">
      <div className="attr-chips">
        {shown.map((f) => {
          const isOpen = open === f.key;
          return (
            <button
              key={f.key}
              type="button"
              className={`chip attr-chip${f.set ? ' attr-chip-set' : ''}`}
              aria-pressed={f.panel ? isOpen : f.set}
              aria-expanded={f.panel ? isOpen : undefined}
              onClick={() =>
                f.panel ? setOpen((cur) => (cur === f.key ? null : f.key)) : f.onToggle?.()
              }
            >
              {f.icon}
              {f.set && f.value != null ? f.value : f.label}
            </button>
          );
        })}
      </div>
      {shown.map(
        (f) =>
          f.panel &&
          open === f.key && (
            <div className="attr-panel" key={f.key}>
              {f.panel}
            </div>
          ),
      )}
    </div>
  );
}
